import { React, useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { RootState } from "../../../redux/reducers/root";
import AmpDropdown, {Option} from "../../common/AmpDropdown/AmpDropdown";
import AmpLoadingSpinner from "../../common/AmpLoadingSpinner/AmpLoadingSpinner";
import { getReport } from "../../../redux/actions/report/report"
import { server } from "../../../redux/middleware/api"
import "./Reports.css";

interface props {
    reports?: {
        loading: boolean,
        reports: []
    },
    report?: {
        loading: boolean,
        report: {}
    },
    testing?: boolean
}

interface MvrDropdownOptions {
    displayName: string, 
    isHeading: boolean, 
    filename: string,
    links: MvrDropdownOptions[]
}


export default function Reports(props: props): JSX.Element {

    const propsReports = props.reports
    const propsReport = props.report
    const testing = props.testing

    const reports = useSelector((state: RootState) => state.repsReducer?.reports)
    const report = useSelector((state: RootState) => state.repReducer?.report)
    
    const dispatch = useDispatch()
    const { t } = useTranslation(["property"])
    
    const repsLoading = (propsReports && propsReports["loading"]) || (reports && reports["loading"])
    const repLoading = (propsReport && propsReport["loading"]) || (report && report["loading"])
    const loading = repsLoading
    const reportsArr = propsReports ? propsReports["reports"] : reports["reports"]
    const reportObj = propsReport ? propsReport["report"] : report["report"]
    const isCurrentReps = reportsArr && (reportsArr["meth"].length || reportsArr["mvrs"].length || reportsArr["lmas"].length)
    const isMvrDropdown = reportsArr && reportsArr["is_mvr_dropdown"]
    const meth = reportsArr && reportsArr["meth"]
    const mvrs = reportsArr && reportsArr["mvrs"]
    const lmas = reportsArr && reportsArr["lmas"]
    const pdf = reportObj && reportObj["pdf"]
    const isLink = reportObj && reportObj["is_link"]
    const isPLink = reportObj && reportObj["Partial_link"]

    const lang = localStorage.getItem("prefLang") || "en";
    const currentProperty = window.localStorage.getItem("currentProperty");

    const [highlighted, setHighlighted] = useState({ filename: "", type: "" })
    const [filename, setFileName] = useState("");
    const [fileSource, setFileSource] = useState("");
    const [currentMvr, setCurrentMvr] = useState("");
    const [showError, setShowError] = useState("");

    useEffect(() => {
        if (filename) {
            const fileArr = filename.split(",")
            setHighlighted({ filename: fileArr[0], type: fileArr[1] })
        } else {
            setHighlighted({ filename: "mvrs", type: "" })
        }
    }, [filename])

    const handleChange = useCallback((e: { filename: string }, isMeth = false) => {
        const docLang = lang === "fr" ? "French" : "English";
        const reportType = e.filename.substring(0, 3) === "MVR" ? "mvrs" : "lma";
        if (currentProperty) {
            const formInput = {
                "roll": currentProperty,
                "is_report": true,
                "is_meth": isMeth,
                "doc_name": e.filename,
                "doc_id": null,
                "property_id": null,
                "lang": docLang
            }
            dispatch(getReport(formInput))
            if(isMvrDropdown && !formInput["is_meth"]){
                setFileName(``);
                setCurrentMvr(e.filename);
            } else {
                setFileName(`${formInput["doc_name"]},${formInput["is_meth"] ? "meth" : reportType},${docLang}`)
            }
        }
    }, [dispatch, lang, currentProperty, isMvrDropdown])

    useEffect(() => {
        if (!report.report && !report.loading) {
            const docLang = lang === "fr" ? "French" : "English";
            const formInput = {
                "roll": currentProperty,
                "is_report": true,
                "is_meth": false,
                "doc_name": null,
                "doc_id": null,
                "property_id": null,
                "lang": docLang
            }
            let initialType;
            if (meth?.length && meth[0]) {
                formInput["is_meth"] = true
                formInput["doc_name"] = meth[0]["filename"]
                initialType = "meth";
            } else if (mvrs?.length && mvrs[0]) {
                formInput["doc_name"] = mvrs[0]["filename"]
                initialType = "mvrs";
            } else if (lmas?.length && lmas[0]) {
                formInput["doc_name"] = lmas[0]["filename"]
                initialType = "lma";
            }
            dispatch(getReport(formInput))
            setFileName(`${formInput["doc_name"]},${initialType},${docLang}`)
        }
    }, [report, dispatch, lmas, meth, mvrs, lang, currentProperty])

    useEffect(() => {
        if (!repLoading && pdf) {
            if (isPLink) {
                setFileSource(`${server}/${pdf}`);
                setShowError("N")
            } else {
                if (isLink){
                    setFileSource(pdf)
                    setShowError("N")
                } else {
                    const string = getFileSource(pdf)
                    fetch(string)
                        .then(res => res.blob())
                        .then(data => {
                            const newurl = window.URL.createObjectURL(data)
                            setFileSource(newurl)
                            setShowError("N")
                        })
                }
            }
        } else if (!repLoading && reportObj?.status === 404) {
            setShowError("Y")
        }
    }, [reportObj, pdf, repLoading, isLink, isPLink])
    const getFileSource = (pdf: string) => {
        return `data:application/pdf;base64,${pdf}`
    }

    const mapOptions = (options: MvrDropdownOptions [], useDefaultHeading: boolean): Option[] => {
        const optionsFormatted: Option[] = []
        if (useDefaultHeading) {
            optionsFormatted.push({ value: "", text: "- Select a report -", disabled: true })
        }
         
        options.forEach((e: MvrDropdownOptions) => {
            let option: Option;
            if (e.isHeading) {
                const suboptions = e.links
                const newOptions = mapOptions(suboptions, false);
                option = { value: "", text: e.displayName, disabled: true, subOptions: newOptions }
            } else {
                option = { value: e.filename || "", text: e.displayName, disabled: false}
            }
            optionsFormatted.push(option)
        })
        return optionsFormatted;
    }

    const getOptions = () => {
        return mapOptions(mvrs, true);
    }
    return (
        <div>
            <AmpLoadingSpinner loading={loading}>
                <div className="documents-container">
                    {!loading && (isCurrentReps === 1 ?
                        <div className="flex-container">
                            <div className="tabs-container">
                                <div className="reports-description">
                                    {t("reports.description") as string}
                                </div>
                                {meth && meth.map((e: { filename: string }, i: number) => {
                                    return <div className="report" id={(highlighted.filename === meth[i].filename || !meth[i].filename) && highlighted.type === "meth" ? "highlighted" : "meth-guide"} onClick={() => {
                                        handleChange(e, true)
                                    }} key={i}>
                                        <p className="report-title">
                                            {(lang === "fr" ? meth[i].desc_fr : meth[i].desc_en)?.toUpperCase() as string}
                                        </p>
                                        <p>{t("reports.residential.first-para") as string}</p>
                                        <p>
                                            {t("reports.residential.second-para") as string}
                                            <a
                                                href="https://www.mpac.ca/en/UnderstandingYourAssessment/2020AssessmentUpdate/Level1disclosuremethodologyguides"
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                className="report-link"
                                            >
                                                {t("reports.residential.link") as string}
                                            </a>
                                        </p>
                                    </div>
                                })}
                                {mvrs && !isMvrDropdown && mvrs.map((e: { filename: string }, i: number) => {
                                    return <>
                                        {meth && <hr className="report-hr" />}
                                        <div className="report" id={(highlighted.filename === mvrs[i].filename || !mvrs[i].filename) && highlighted.type === "mvrs" ? "highlighted" : "mvr-guide"} onClick={() => {
                                            handleChange(e)
                                        }} key={i}>
                                            <p className="report-title">
                                                {(lang === "fr" ? mvrs[i].desc_fr : mvrs[i].desc_en)?.toUpperCase() as string}
                                            </p>
                                            <p>{t("reports.market-valuation-report.first-para") as string}</p>
                                            <p>
                                                {t("reports.market-valuation-report.fourth-para") as string}
                                            </p>
                                        </div>
                                    </>
                                })}
                                {mvrs && isMvrDropdown &&
                                    <>
                                        {meth && <hr className="report-hr" />}
                                        <div className="report" id={highlighted.filename === "mvrs" ? "highlighted" : "mvr-guide"} onClick={() => {
                                            handleChange({ filename: currentMvr })
                                        }}>
                                            <p className="report-title">
                                                {(t("reports.market-valuation-report.title") as string)?.toUpperCase()}
                                            </p>
                                            <p>{t("reports.market-valuation-report.first-para") as string}{" "}{t("reports.market-valuation-report.second-para") as string}</p>
                                            <p>{t("reports.market-valuation-report.third-para") as string}</p>
                                            <p onClick={(e) => e.stopPropagation()}>
                                                <AmpDropdown className="select-box"
                                                    value={currentMvr} options={getOptions()} id="report-dropdown"
                                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                        handleChange({ filename: e.target.value }, false)
                                                        // setCurrentMvr();
                                                    }
                                                    } /></p>
                                            <p>{t("reports.market-valuation-report.fourth-para") as string}</p>
                                        </div>
                                    </>
                                }
                                {lmas && lmas.map((e: { filename: string }, i: number) => {
                                    return <>
                                        {meth && <hr className="report-hr" />}
                                        <div className="report" id={highlighted.filename === lmas[i].filename ? "highlighted" : "report-guide"} onClick={() => {
                                            handleChange(e)
                                        }} key={i}>
                                            <p className="report-title">
                                                {(lang === "fr" ? lmas[i].desc_fr : lmas[i].desc_en)?.toUpperCase() as string}
                                            </p>
                                            <p>{t("reports.local-market-adjustments-report.first-para") as string}</p>
                                        </div>
                                    </>
                                })}
                            </div>
                            <AmpLoadingSpinner loading={repLoading || !showError}>
                                <div className="pdf-container">
                                    {/*eslint-disable-next-line*/}
                                    {(showError === "N" || testing) && <iframe src={fileSource} width="100%" height="100%" aria-label="reports-container" />}
                                    {showError === "Y" && <h1 className="not-available">{t("reports.not-available") as string}</h1>}
                                </div>
                            </AmpLoadingSpinner>
                        </div> : <div className="rfr-notice-na">
                            <FontAwesomeIcon icon={faCircleExclamation} size="5x" className="rfr-notice-na-icon" />
                            <h2>{t("reports.noreports")}</h2>
                        </div>
                    )}
                </div>
            </AmpLoadingSpinner>
        </div>
    )
}
